import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, LogIn, KeyRound, ScanLine, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import TimeclockQRDisplay from '@/pages/public/TimeclockQRDisplay';

const ScanPage = ({ onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(() => localStorage.getItem('vortekio_email') || '');
  const [pinCode, setPinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [canShowQR, setCanShowQR] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/timeclock/check-ip`).then(res => {
      setCanShowQR(res.data.allowed);
    }).catch(() => setCanShowQR(false));
    setTimeout(() => setMounted(true), 100);
  }, []);

  const handleLogin = async () => {
    if (!email.trim()) { toast.error(t('auth.enter_email')); return; }
    if (!pinCode || pinCode.length !== 4) { toast.error(t('auth.pin_required')); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/pin-login`, {
        email: email.trim().toLowerCase(),
        pin_code: pinCode,
      });
      const { access_token, employee } = response.data;
      localStorage.setItem('vortekio_email', email.trim().toLowerCase());
      onLogin(access_token, employee);
      toast.success(t('auth.welcome_user', { name: employee.first_name }));
      navigate('/employee/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || t('auth.login_error');
      toast.error(detail);
      if (error.response?.status === 401) setPinCode('');
    } finally {
      setLoading(false);
    }
  };

  const isMobile = window.innerWidth <= 640;
  const bgImage = isMobile ? '/vortekio-bg-mobile.png' : '/vortekio-bg.png';

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-8px) rotate(1deg); }
          66% { transform: translateY(4px) rotate(-0.5deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59,130,246,0.0); }
          50% { box-shadow: 0 0 40px rgba(59,130,246,0.3); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dot-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .scan-bg-overlay {
          background: linear-gradient(135deg, rgba(15,23,42,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(15,23,42,0.5) 100%);
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }
        .logo-float { animation: float 6s ease-in-out infinite; }
        .card-glow { animation: pulse-glow 4s ease-in-out infinite; }
        .shimmer-text {
          background: linear-gradient(90deg, #94a3b8 0%, #e2e8f0 25%, #ffffff 50%, #e2e8f0 75%, #94a3b8 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 4s linear infinite;
        }
        .dot-anim { animation: dot-pulse 2s ease-in-out infinite; }
        .slide-1 { animation: slide-up 0.6s ease-out 0.1s both; }
        .slide-2 { animation: slide-up 0.6s ease-out 0.25s both; }
        .slide-3 { animation: slide-up 0.6s ease-out 0.4s both; }
        .slide-4 { animation: slide-up 0.6s ease-out 0.55s both; }
        .fade-slow { animation: fade-in 1.2s ease-out both; }

        .login-input:focus-within {
          border-color: rgba(59,130,246,0.5) !important;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.15), inset 0 0 20px rgba(59,130,246,0.05);
        }
        .login-btn {
          background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #1d4ed8 100%);
          background-size: 200% 200%;
          transition: all 0.3s ease;
        }
        .login-btn:hover:not(:disabled) {
          background-position: 100% 50%;
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(37,99,235,0.4);
        }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      <div
        className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Animated overlay */}
        <div className="absolute inset-0 scan-bg-overlay" />

        {/* Language selector */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSelector />
        </div>

        {/* Floating light particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${3 + i * 2}px`,
                height: `${3 + i * 2}px`,
                background: `rgba(${i % 2 ? '239,68,68' : '59,130,246'}, ${0.15 + i * 0.05})`,
                left: `${15 + i * 18}%`,
                top: `${20 + i * 12}%`,
                animation: `float ${5 + i}s ease-in-out ${i * 0.8}s infinite`,
                filter: `blur(${1 + i * 0.5}px)`,
              }}
            />
          ))}
        </div>

        <div className={`w-full flex flex-col items-center relative z-10 transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          {/* Logo with float animation */}
          <div className="logo-float mb-1">
            <div className="fade-slow" style={{ fontSize: 0, lineHeight: 0 }}>
              <span
                className="inline-block font-black tracking-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(3.5rem, 10vw, 7rem)', lineHeight: 1, textShadow: '0 0 40px rgba(255,255,255,0.1)' }}
                data-testid="login-logo"
              >
                vortek<span style={{ position: 'relative', display: 'inline-block' }}>&#305;<span style={{ position: 'absolute', top: '0.05em', left: '50%', transform: 'translateX(-50%)', width: '0.18em', height: '0.18em', borderRadius: '50%', background: '#ef4444', display: 'block' }} /></span>o<span style={{ color: '#ef4444' }}>.</span>
              </span>
            </div>
          </div>

          {/* Animated tagline */}
          <p className="shimmer-text text-sm sm:text-base font-medium tracking-wider mb-4 slide-1">
            {t('auth.tagline')}
          </p>

          {/* Animated red dot separator */}
          <div className="flex items-center gap-2 mb-5 slide-2">
            <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-red-500/60" />
            <div className="w-2 h-2 rounded-full bg-red-500 dot-anim" />
            <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-red-500/60" />
          </div>

          <div className="w-full max-w-xs flex flex-col items-center">
            {/* Login card with glow */}
            <div
              className="w-full p-5 rounded-2xl space-y-4 card-glow slide-3"
              style={{
                background: 'rgba(15, 20, 30, 0.75)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <p className="text-center text-slate-400 text-xs tracking-widest uppercase">{t('auth.employee_login')}</p>

              <div className="space-y-3" data-testid="login-form">
                <div className="space-y-1">
                  <label className="flex items-center space-x-1.5 text-slate-300">
                    <Mail className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs font-medium">{t('auth.email')}</span>
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.email_placeholder')}
                    className="h-10 rounded-xl text-sm bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500 login-input transition-all duration-300"
                    data-testid="email-input"
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-1">
                  <label className="flex items-center space-x-1.5 text-slate-300">
                    <KeyRound className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs font-medium">{t('auth.pin_code')}</span>
                  </label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="h-12 text-center text-2xl tracking-[0.5em] rounded-xl font-mono bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500 login-input transition-all duration-300"
                    data-testid="pin-input"
                    onKeyDown={(e) => e.key === 'Enter' && email && pinCode.length === 4 && handleLogin()}
                  />
                </div>

                <Button
                  onClick={handleLogin}
                  disabled={!email.trim() || !pinCode || pinCode.length !== 4 || loading}
                  className="w-full h-10 rounded-xl font-semibold text-sm text-white login-btn border-0"
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('auth.logging_in')}
                    </span>
                  ) : (
                    <>{t('auth.login')} <LogIn className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-center space-y-1.5 mt-3 slide-4">
              {canShowQR && (
                <Button
                  onClick={() => setShowQR(true)}
                  className="w-full h-10 rounded-xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20"
                  data-testid="open-timeclock-qr-btn"
                >
                  <ScanLine className="mr-2 w-4 h-4" />
                  {t('auth.timeclock')}
                </Button>
              )}
              <Button
                variant="link"
                onClick={() => navigate('/admin/login')}
                className="text-xs text-slate-400 hover:text-white transition-colors duration-300"
                data-testid="admin-login-link"
              >
                {t('auth.admin_access')}
              </Button>
            </div>
          </div>
        </div>

        {showQR && (
          <div className="fixed inset-0 z-50" data-testid="qr-overlay">
            <TimeclockQRDisplay />
            <Button
              onClick={() => setShowQR(false)}
              className="fixed top-4 right-4 z-50 rounded-full w-12 h-12 bg-slate-800/80 hover:bg-slate-700 border border-slate-600 text-white"
              data-testid="close-qr-btn"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default ScanPage;
